import React from "react";
import { Link } from "react-router-dom";
import GlobeCanvas from "../components/GlobeCanvas";
import { SiteHeader, SiteFooter, Icon } from "../components/SiteChrome";

const modules = [
  {
    id: "air",
    title: "Air quality",
    source: "OPENWEATHER · CAMS",
    desc: "Put the air around you in perspective. Explore pollutants, air-quality indices and forecasts.",
    color: "teal",
  },
  {
    id: "climate",
    title: "Weather & climate",
    source: "OPEN-METEO",
    desc: "From current model estimates to the week ahead: temperature, rain, humidity and wind.",
    color: "amber",
  },
  {
    id: "flood",
    title: "River flow",
    source: "GLOFAS",
    desc: "Follow river discharge with recent history, a 14-day outlook and forecast uncertainty.",
    color: "blue",
  },
  {
    id: "veg",
    title: "Soil & vegetation",
    source: "OPEN-METEO",
    desc: "Look beneath the surface with shallow soil moisture, rainfall and water-demand indicators.",
    color: "green",
  },
  {
    id: "drought",
    title: "Drought context",
    source: "HISTORICAL WEATHER",
    desc: "Explore recent rainfall and climatic water balance over the past 30 and 90 days.",
    color: "violet",
  },
];

export default function LandingPage() {
  return (
    <div className="site-page">
      <SiteHeader />
      <main id="main-content">
        <section className="hero-section">
          <div className="landing-hero site-container">
            <div className="hero-copy">
              <div className="hero-label">
                <span className="tiny-dot" /> YOUR WORLD, IN PERSPECTIVE
              </div>
              <h1>
                A changing planet.
                <br />A <span>clearer view.</span>
              </h1>
              <p>
                Understand the environment around you. Explore air, weather,
                water and soil through one connected view of the world.
              </p>
              <div className="hero-actions">
                <Link className="primary-button" to="/monitor">
                  Explore the monitor <Icon name="arrow" size={19} />
                </Link>
                <Link className="text-link" to="/modules">
                  Discover the modules <Icon name="arrow" size={17} />
                </Link>
              </div>
              <div className="hero-caption">
                <span>
                  <Icon name="globe" size={16} /> Global models
                </span>
                <span>
                  <Icon name="layers" size={16} /> Five perspectives
                </span>
                <span>
                  <Icon name="check" size={16} /> Sources included
                </span>
              </div>
            </div>
            <div className="globe-panel">
              <div className="globe-kicker">
                <span>THE BIG PICTURE</span>
                <span>01 — 05</span>
              </div>
              <GlobeCanvas />
              <div className="globe-badge globe-badge-air">
                <span className="badge-icon teal">
                  <Icon name="air" />
                </span>
                <span>
                  <b>Atmosphere</b>
                  <small>Air & weather</small>
                </span>
              </div>
              <div className="globe-badge globe-badge-land">
                <span className="badge-icon violet">
                  <Icon name="veg" />
                </span>
                <span>
                  <b>Land & water</b>
                  <small>Connected perspectives</small>
                </span>
              </div>
              <div className="globe-caption">
                <span className="tiny-dot" /> One planet. Many signals.
              </div>
            </div>
          </div>
        </section>
        <section
          className="provider-strip"
          aria-label="Environmental data providers"
        >
          <div className="site-container provider-inner">
            <span>
              BUILT ON ESTABLISHED
              <br />
              ENVIRONMENTAL DATA
            </span>
            <b>OpenWeather</b>
            <b>Open-Meteo</b>
            <b>
              Copernicus <small>CAMS</small>
            </b>
            <b>GloFAS</b>
          </div>
        </section>
        <section className="modules-section site-container" id="modules">
          <div className="section-intro">
            <div>
              <div className="eyebrow">FIVE WAYS TO EXPLORE</div>
              <h2>
                A fuller picture.
                <br />
                One place to start.
              </h2>
            </div>
            <p>
              Different systems. Connected stories. Choose a module to explore
              current estimates, forecasts or historical context for a location.
            </p>
          </div>
          <div className="module-card-grid">
            {modules.map((mod, i) => (
              <Link
                className={`module-card ${mod.color}`}
                to={`/monitor?module=${mod.id}`}
                key={mod.id}
              >
                <div className="module-card-top">
                  <span className="module-icon">
                    <Icon name={mod.id} size={25} />
                  </span>
                  <span>0{i + 1}</span>
                </div>
                <h3>{mod.title}</h3>
                <div className="module-source">{mod.source}</div>
                <p>{mod.desc}</p>
                <span className="module-open">
                  Explore <Icon name="arrow" size={18} />
                </span>
              </Link>
            ))}
          </div>
        </section>
        <section className="how-section">
          <div className="site-container">
            <div className="section-intro">
              <div>
                <div className="eyebrow">FROM LOCATION TO CONTEXT</div>
                <h2>Curiosity is a good starting point.</h2>
              </div>
              <Link className="text-link" to="/monitor">
                Take a closer look <Icon name="arrow" size={18} />
              </Link>
            </div>
            <div className="how-grid">
              {[
                {
                  num: "01",
                  icon: "pin",
                  title: "Find your place",
                  text: "Choose a city, tap the map or enter coordinates. Start with somewhere that matters to you.",
                },
                {
                  num: "02",
                  icon: "layers",
                  title: "Choose a perspective",
                  text: "Switch between five environmental modules to explore the same location through different datasets.",
                },
                {
                  num: "03",
                  icon: "clock",
                  title: "Read with context",
                  text: "See the provider, data time and availability alongside every result. Understand what the numbers represent.",
                },
              ].map((step) => (
                <article className="how-card" key={step.num}>
                  <div>
                    <span className="step-number">{step.num}</span>
                    <Icon name={step.icon} size={27} />
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        <section className="trust-section">
          <div className="site-container trust-inner">
            <div>
              <div className="eyebrow">CLARITY, BY DESIGN</div>
              <h2>
                Good decisions start
                <br />
                with understanding
                <br />
                <span>the data.</span>
              </h2>
              <Link className="light-button" to="/modules#data">
                Meet the sources <Icon name="arrow" size={18} />
              </Link>
            </div>
            <div className="trust-details">
              <article>
                <span className="trust-icon">
                  <Icon name="clock" />
                </span>
                <div>
                  <h3>Freshness you can see</h3>
                  <p>
                    Data time and fetch time are shown separately. The monitor
                    checks for updates every five minutes while visible; each
                    provider has its own update schedule.
                  </p>
                </div>
              </article>
              <article>
                <span className="trust-icon">
                  <Icon name="layers" />
                </span>
                <div>
                  <h3>Models with their context</h3>
                  <p>
                    Current estimates, forecasts and historical records are
                    labelled. A model grid represents an area, rather than a
                    sensor at every point.
                  </p>
                </div>
              </article>
              <article>
                <span className="trust-icon">
                  <Icon name="check" />
                </span>
                <div>
                  <h3>A clear view of what is available</h3>
                  <p>
                    Missing, older or unavailable data is flagged. A refresh
                    checks the source; it does not create a new measurement.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>
        <section className="faq-section site-container">
          <div>
            <div className="eyebrow">A LITTLE MORE CONTEXT</div>
            <h2>Before you explore.</h2>
            <p>Useful answers about the view ahead.</p>
          </div>
          <div className="faq-list">
            <details>
              <summary>Is AirVista showing real-time data?</summary>
              <p>
                AirVista requests real external APIs. Most results are model
                estimates, forecasts or historical data, rather than a live
                sensor stream. Source timestamps and update schedules help you
                judge how current each result is.
              </p>
            </details>
            <details>
              <summary>Can I explore any location?</summary>
              <p>
                You can choose global coordinates. Availability and detail vary
                by provider and model. River data may represent a nearby river
                grid, while some locations may have missing results.
              </p>
            </details>
            <details>
              <summary>How should I use the information?</summary>
              <p>
                Use AirVista to explore and compare environmental context.
                Models can differ from local observations. For safety-critical
                decisions, consult local authorities and official alerts.
              </p>
            </details>
          </div>
        </section>
        <section className="cta-section site-container">
          <div className="cta-panel">
            <div>
              <div className="eyebrow">YOUR NEXT PERSPECTIVE</div>
              <h2>Start with a place you know.</h2>
              <p>A city. A river basin. Somewhere you call home.</p>
            </div>
            <Link className="primary-button" to="/monitor">
              Open the monitor <Icon name="arrow" size={19} />
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
